/**
 * Salvage a user-facing answer out of leaked agent scaffolding.
 *
 * A ReAct agent is supposed to send us a finished answer. When its own decision
 * parser fails, the raw transcript can arrive instead — `<function_calls>`
 * blocks of thoughts, tool calls and observations, with the real answer buried
 * in a `final` field. Rendering that verbatim is the worst outcome: the markdown
 * table inside it can't even line-break, because those `\n` are escape
 * sequences inside an unparsed JSON string.
 *
 * The upstream cause is fixed in the orchestrator (agent/orchestrator/
 * decision_parse.py). This module is the frontend's standing guarantee that a
 * future regression there — or any other producer that leaks machinery — still
 * shows the user an answer and an activity timeline rather than a wall of JSON.
 *
 * Shapes handled are pinned by name in agentScaffolding.test.js. Add drift to
 * that corpus rather than special-casing it at a call site.
 */

// Narrow on purpose: a bare '<' or the word "action" would match ordinary
// answers like "the count is 5 < 10".
const TAG_RE = /<\/?(?:function_calls|function_results|invoke|parameter|tool_calls?|antml:\w+)\b[^>]*>/gi;
const NOISE_TAG_RE = /<\/?(?:function_calls|function_results|invoke|parameter|tool_calls?|antml:\w+)\b/i;
const NOISE_KEY_RE = /["']observation["']\s*:/i;
const NOISE_TEXT_RE = /^\s*Observation\s*:/im;

const THOUGHT_KEYS = ['thoughts', 'thought', 'reasoning'];
const ACTION_KEYS = ['action', 'tool', 'tool_name', 'function'];
const ARGS_KEYS = ['action_input', 'tool_input', 'arguments', 'args', 'parameters', 'input'];
const FINAL_KEYS = ['final', 'final_answer'];
const OBSERVATION_KEYS = ['observation', 'observations', 'result'];

const INVOKE_RE = /<invoke\s+name=["']([^"']+)["']\s*>([\s\S]*?)<\/invoke\s*>/gi;
const PARAM_RE = /<parameter\s+name=["']([^"']+)["']\s*>([\s\S]*?)<\/parameter\s*>/gi;

/** Is this text agent machinery rather than prose meant for a person? */
export const hasAgentScaffolding = (text) => {
  if (typeof text !== 'string' || !text.trim()) return false;
  return NOISE_TAG_RE.test(text) || NOISE_KEY_RE.test(text) || NOISE_TEXT_RE.test(text);
};

const firstKey = (obj, keys) => keys.find((k) => obj[k] !== undefined && obj[k] !== null && obj[k] !== '');

/**
 * Every balanced `{...}` / `[...]` region in the text, outermost only.
 *
 * A regex can't match balanced braces, and the payloads nest (an array holding
 * an object holding an args object), so this scans with a depth counter and
 * skips over string literals so a brace inside a JSON string can't unbalance it.
 */
const jsonRegions = (text) => {
  const out = [];
  let depth = 0;
  let start = -1;
  let inString = false;
  let escaped = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (inString) {
      if (escaped) escaped = false;
      else if (ch === '\\') escaped = true;
      else if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') { inString = true; continue; }
    if (ch === '{' || ch === '[') {
      if (depth === 0) start = i;
      depth += 1;
    } else if (ch === '}' || ch === ']') {
      if (depth > 0) {
        depth -= 1;
        if (depth === 0 && start !== -1) {
          out.push({ start, end: i + 1, text: text.slice(start, i + 1) });
          start = -1;
        }
      }
    }
  }
  return out;
};

/** Flatten an arbitrarily nested parse result to its dicts, in document order. */
const flattenDicts = (value, acc = []) => {
  if (Array.isArray(value)) value.forEach((v) => flattenDicts(v, acc));
  else if (value && typeof value === 'object') acc.push(value);
  return acc;
};

/**
 * Normalise a salvaged args payload to the object shape a tool step carries, so
 * salvaged rows render through the same parameters block as backend-typed ones.
 * A bare string (some models emit raw SQL as the whole input) becomes `{input}`
 * rather than being dropped or stringified into the row body.
 */
const toArgsObject = (args) => {
  if (args == null || args === '') return null;
  if (typeof args === 'string') return { input: args };
  if (typeof args === 'object' && !Array.isArray(args)) return args;
  return { input: JSON.stringify(args) };
};

const stripTags = (text) => text.replace(TAG_RE, ' ');

// A whole tag block, content included. Needed for the `<invoke>` shape, where
// the arguments live as `<parameter>` TEXT rather than in JSON — stripping only
// the tags would leave bare argument values sitting in the answer.
const FULL_BLOCK_RE = /<(function_calls|function_results|invoke|parameter|tool_calls?)\b[^>]*>[\s\S]*?<\/\1\s*>/gi;

/**
 * Split leaked scaffolding into an answer and a step timeline.
 *
 * @param {string} text - raw answer text, scaffolding or not
 * @returns {{answer: string, steps: Array, hadScaffolding: boolean}} steps use
 *   the same `{type, content, tool, status, ts}` shape as the persisted
 *   reasoning-step timeline, so they render through the same component.
 */
export const parseAgentScaffolding = (text) => {
  if (typeof text !== 'string' || !text.trim()) {
    return { answer: '', steps: [], hadScaffolding: false };
  }
  if (!hasAgentScaffolding(text)) {
    return { answer: text, steps: [], hadScaffolding: false };
  }

  const steps = [];
  let finalAnswer = null;
  const consumed = [];

  jsonRegions(text).forEach((region) => {
    let parsed;
    try {
      parsed = JSON.parse(region.text);
    } catch {
      // Machinery we can't parse is still machinery: mark it consumed so it
      // can't fall through into the answer, and move on.
      consumed.push(region);
      return;
    }
    consumed.push(region);

    flattenDicts(parsed).forEach((obj) => {
      const thoughtKey = firstKey(obj, THOUGHT_KEYS);
      if (thoughtKey) {
        steps.push({ type: 'thought', content: String(obj[thoughtKey]) });
      }

      const actionKey = firstKey(obj, ACTION_KEYS);
      if (actionKey) {
        const argsKey = firstKey(obj, ARGS_KEYS);
        const args = toArgsObject(argsKey ? obj[argsKey] : null);
        steps.push({
          type: 'tool',
          tool: String(obj[actionKey]),
          status: 'ok',
          ...(args ? { args } : {}),
          content: '',
        });
      }

      const obsKey = firstKey(obj, OBSERVATION_KEYS);
      if (obsKey) {
        // An observation belongs to the call that produced it. Appending to that
        // step keeps args and result in one expandable row.
        const lastTool = [...steps].reverse().find((s) => s.type === 'tool');
        const observation = String(obj[obsKey]);
        if (lastTool) {
          lastTool.content = [lastTool.content, observation].filter(Boolean).join('\n\n');
        } else {
          steps.push({ type: 'tool', tool: 'result', status: 'ok', content: observation });
        }
      }

      const finalKey = firstKey(obj, FINAL_KEYS);
      if (finalKey) finalAnswer = String(obj[finalKey]);
    });
  });

  [...text.matchAll(INVOKE_RE)].forEach((m) => {
    const args = {};
    [...m[2].matchAll(PARAM_RE)].forEach(([, k, v]) => { args[k] = v.trim(); });
    steps.push({
      type: 'tool',
      tool: m[1].trim(),
      status: 'ok',
      ...(Object.keys(args).length ? { args } : {}),
      content: '',
    });
  });

  // The answer is the `final` field when the transcript carried one. Otherwise
  // fall back to whatever prose sits outside the machinery — a model that
  // narrates its steps often still writes the real answer after them.
  let answer = finalAnswer;
  if (answer == null) {
    let residue = '';
    let cursor = 0;
    consumed
      .sort((a, b) => a.start - b.start)
      .forEach((region) => {
        residue += text.slice(cursor, region.start);
        cursor = region.end;
      });
    residue += text.slice(cursor);
    answer = stripTags(residue.replace(FULL_BLOCK_RE, ' ')).replace(/[ \t]+\n/g, '\n').trim();
  }

  // Belt and braces: whatever we picked, it must not carry machinery onward.
  answer = stripTags(answer || '').trim();

  return { answer, steps, hadScaffolding: true };
};
